import { catchError, concat, Observable, of } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { ENV } from '@components/Environment.js';
import { CacheStorageAdapter_IndexedDB } from '@components/CacheManager/CacheStorageAdapter_IndexedDB.js';
import DIContainer from '@components/DependencyInjectionContainer.js';
import { capitalizeFirstLetter, isArray } from '@components/Utility/index.js';
export class CacheManager {
    constructor(dbName) {
        this.storeAdapters = new Map();
        this.pendingStoreNotifications = new Set();
        this.defaultAdapter = new CacheStorageAdapter_IndexedDB(dbName);
        this.isReady$ = this.defaultAdapter.isReady$;
        this.initializeStoreAdapters();
    }
    notifyStoreChanged(cacheName) {
        this.pendingStoreNotifications.add(cacheName);
        if (!this.storeNotificationHandle) {
            this.storeNotificationHandle = setTimeout(() => {
                this.storeNotificationHandle = undefined;
                const stores = [...this.pendingStoreNotifications];
                this.pendingStoreNotifications.clear();
                const broadcastManager = DIContainer.getInstance('BroadcastManager');
                for (const store of stores) {
                    broadcastManager.send(`cache_change:${store}`, { payload: {} });
                }
            }, 0);
        }
    }
    getStoreKeyPath(storeName) {
        return this.defaultAdapter.getStoreKeyPath(storeName);
    }
    getStoreIndexes(storeName) {
        return this.defaultAdapter.getStoreIndexes(storeName);
    }
    initializeStoreAdapters() {
        if (!ENV('isNative'))
            return;
        for (const storeName of this.defaultAdapter.getStoreNames()) {
            const adapterKey = `CacheStorageAdapter_${capitalizeFirstLetter(storeName)}`;
            if (DIContainer.isRegistered(adapterKey)) {
                this.registerStoreAdapter(storeName, DIContainer.getInstance(adapterKey));
            }
        }
    }
    registerStoreAdapter(storeName, adapter) {
        this.storeAdapters.set(storeName, adapter);
    }
    getStoreAdapter(storeName) {
        return this.storeAdapters.get(storeName) ?? this.defaultAdapter;
    }
    formatIndexKeyForEvent(indexKey) {
        const raw = Array.isArray(indexKey) ? JSON.stringify(indexKey) : String(indexKey);
        return encodeURIComponent(raw);
    }
    getIndexEventName(cacheName, indexName, indexKey) {
        return `cache_index_change:${cacheName}:${indexName}:${this.formatIndexKeyForEvent(indexKey)}`;
    }
    extractIndexKey(indexDef, value) {
        if (typeof indexDef.keyPath === 'string') {
            const out = value?.[indexDef.keyPath];
            return typeof out === 'undefined' ? null : out;
        }
        const parts = indexDef.keyPath.map((path) => value?.[path]);
        if (parts.some((part) => typeof part === 'undefined'))
            return null;
        return parts;
    }
    getIndexEventNames(cacheName, value) {
        const events = [];
        const indexes = this.getStoreIndexes(cacheName);
        for (const indexDef of indexes) {
            const indexKey = this.extractIndexKey(indexDef, value);
            if (indexKey === null)
                continue;
            events.push(this.getIndexEventName(cacheName, indexDef.name, indexKey));
        }
        return events;
    }
    extractKeyFromValue(keyPath, fallbackKey, value) {
        if (typeof keyPath !== 'string')
            return fallbackKey;
        const v = value?.[keyPath];
        return (typeof v === 'string' && v) ? v : fallbackKey;
    }
    assertKeyPathMatches(keyPath, key, value) {
        if (typeof keyPath !== 'string')
            return;
        const v = value?.[keyPath];
        if (typeof v === 'undefined') {
            value[keyPath] = key;
            return;
        }
        if (String(v) !== String(key)) {
            throw new Error(`IndexedDB key mismatch for store "${keyPath}": passed key="${key}" but value.${keyPath}="${v}"`);
        }
    }
    setCache(cacheName, key, value) {
        const broadcastManager = DIContainer.getInstance('BroadcastManager');
        const keyPath = this.getStoreKeyPath(cacheName);
        const eventKey = keyPath ? this.extractKeyFromValue(keyPath, key, value) : key;
        const eventName = `cache_change:${cacheName}:${eventKey}`;
        const adapter = this.getStoreAdapter(cacheName);
        if (keyPath) {
            this.assertKeyPathMatches(keyPath, key, value);
        }
        const indexEventNames = this.getIndexEventNames(cacheName, value);
        return adapter.setCache(cacheName, key, value).pipe(tap(() => {
            broadcastManager.send(eventName, { payload: {} });
            for (const indexEventName of indexEventNames) {
                broadcastManager.send(indexEventName, { payload: {} });
            }
            this.notifyStoreChanged(cacheName);
        }));
    }
    // set multiple key/value pairs into a cache at once in a single transaction
    setCacheBatch(cacheName, keyValuePairs) {
        const broadcastManager = DIContainer.getInstance('BroadcastManager');
        const keyPath = this.getStoreKeyPath(cacheName);
        const adapter = this.getStoreAdapter(cacheName);
        const eventNames = new Set();
        for (const key in keyValuePairs) {
            if (!Reflect.has(keyValuePairs, key))
                continue;
            const value = keyValuePairs[key];
            if (keyPath) {
                this.assertKeyPathMatches(keyPath, key, value);
            }
            const eventKey = keyPath ? this.extractKeyFromValue(keyPath, key, value) : key;
            eventNames.add(`cache_change:${cacheName}:${eventKey}`);
            for (const indexEventName of this.getIndexEventNames(cacheName, value)) {
                eventNames.add(indexEventName);
            }
        }
        return adapter.setCacheBatch(cacheName, keyValuePairs).pipe(tap(() => {
            for (const ev of eventNames) {
                broadcastManager.send(ev, { payload: {} });
            }
            this.notifyStoreChanged(cacheName);
        }));
    }
    getCache(cacheName, key, options = {}) {
        if (ENV('disableCache') && cacheName === 'request') {
            if (Reflect.has(options, 'defaultReturnValue')) {
                return of(options.defaultReturnValue);
            }
            return new Observable(subscriber => {
                subscriber.error('Cache is disabled.');
            });
        }
        const adapter = this.getStoreAdapter(cacheName);
        return adapter.getCache(cacheName, key, options).pipe(catchError((e) => {
            if (Reflect.has(options, 'defaultReturnValue')) {
                return of(options.defaultReturnValue);
            }
            throw e;
        }));
    }
    /**
     * Emits the current cache value and then listens for future changes broadcast
     * across tabs for the given cache key.
     *
     * If options.defaultReturnValue is provided and getCache returns nothing,
     * that default will be used.
     *
     * @param cacheName - The name of the cache (object store)
     * @param key - The key within the cache
     * @param options - Optional options, e.g. defaultReturnValue.
     */
    getCache$(cacheName, key, options = {}) {
        const broadcastManager = DIContainer.getInstance('BroadcastManager');
        const eventName = `cache_change:${cacheName}:${key}`;
        // Observable for the initial cache value from IndexedDB.
        const initial$ = this.getCache(cacheName, key, options);
        // Observable that listens for broadcast events with updated values.
        const broadcast$ = broadcastManager.listen(eventName).pipe(switchMap(() => this.getCache(cacheName, key, options)));
        // Concat the initial value (which should complete after one emission)
        // with the broadcast stream for future changes.
        return concat(initial$, broadcast$);
    }
    deleteCache(cacheName, key) {
        const broadcastManager = DIContainer.getInstance('BroadcastManager');
        if (!cacheName) {
            return this.defaultAdapter.deleteAllStores();
        }
        const adapter = this.getStoreAdapter(cacheName);
        if (key) {
            const hasIndexes = this.getStoreIndexes(cacheName).length > 0;
            if (!hasIndexes) {
                return adapter.deleteCache(cacheName, key).pipe(tap(() => this.notifyStoreChanged(cacheName)));
            }
            return adapter.getCache(cacheName, key, { defaultReturnValue: undefined }).pipe(switchMap((value) => {
                const indexEventNames = typeof value === 'undefined'
                    ? []
                    : this.getIndexEventNames(cacheName, value);
                return adapter.deleteCache(cacheName, key).pipe(tap(() => {
                    for (const indexEventName of indexEventNames) {
                        broadcastManager.send(indexEventName, { payload: {} });
                    }
                    this.notifyStoreChanged(cacheName);
                }));
            }));
        }
        return adapter.deleteCache(cacheName).pipe(tap(() => this.notifyStoreChanged(cacheName)));
    }
    // Delete every entry in a store whose key starts with `prefix`.
    // Emits an array of the keys that were deleted.
    deleteCacheByPrefix(cacheName, prefix) {
        const broadcastManager = DIContainer.getInstance('BroadcastManager');
        const adapter = this.getStoreAdapter(cacheName);
        return adapter.deleteCacheByPrefix(cacheName, prefix).pipe(tap((entries) => {
            for (const entry of entries) {
                const indexEventNames = this.getIndexEventNames(cacheName, entry.value);
                for (const indexEventName of indexEventNames) {
                    broadcastManager.send(indexEventName, { payload: {} });
                }
            }
            if (entries.length)
                this.notifyStoreChanged(cacheName);
        }), switchMap((entries) => of(entries.map((entry) => entry.key))));
    }
    deleteCacheByPredicate(cacheName, predicate) {
        const broadcastManager = DIContainer.getInstance('BroadcastManager');
        const adapter = this.getStoreAdapter(cacheName);
        return adapter.deleteCacheByPredicate(cacheName, predicate).pipe(tap((entries) => {
            for (const entry of entries) {
                const indexEventNames = this.getIndexEventNames(cacheName, entry.value);
                for (const indexEventName of indexEventNames) {
                    broadcastManager.send(indexEventName, { payload: {} });
                }
            }
            if (entries.length)
                this.notifyStoreChanged(cacheName);
        }), switchMap((entries) => of(entries.map((entry) => entry.key))));
    }
    wireInvalidationRule(rule) {
        const bm = DIContainer.getInstance('BroadcastManager');
        const cm = this;
        const reg = DIContainer.getInstance('ComponentRegistry');
        rule.events.forEach((eventName) => {
            bm.listen(eventName).subscribe((broadcast) => {
                const payloadArr = isArray(broadcast.payload)
                    ? broadcast.payload
                    : [broadcast.payload];
                const scopes = rule.getScopes(payloadArr)
                    .map(s => (s || '').toLowerCase())
                    .filter(Boolean);
                // cache invalidation
                cm.deleteCacheByPredicate('request', ({ key }) => {
                    key = (key || '').toLowerCase();
                    return rule.shouldDeleteCacheKey(key, scopes);
                }).pipe(tap(() => {
                    // component refresh
                    reg.forEach((component) => {
                        if (rule.shouldRefreshComponent(component, scopes)) {
                            (rule.refreshComponent ?? ((c) => c.refreshData()))(component);
                        }
                    });
                })).subscribe();
            });
        });
    }
    getAll(storeName) {
        const adapter = this.getStoreAdapter(storeName);
        return adapter.getAll(storeName);
    }
    getAllByIndex(storeName, indexName, query) {
        const adapter = this.getStoreAdapter(storeName);
        return adapter.getAllByIndex(storeName, indexName, query);
    }
    getIndexKeyPath(storeName, indexName) {
        const indexes = this.getStoreIndexes(storeName);
        const idx = indexes.find(i => i.name === indexName);
        return idx?.keyPath;
    }
    getAllByIndex$(storeName, indexName, key) {
        const broadcastManager = DIContainer.getInstance('BroadcastManager');
        const eventName = this.getIndexEventName(storeName, indexName, key);
        const initial$ = this.getAllByIndex(storeName, indexName, key);
        const broadcast$ = broadcastManager.listen(eventName).pipe(switchMap(() => this.getAllByIndex(storeName, indexName, key)));
        return concat(initial$, broadcast$);
    }
    upsertRecords(storeName, data) {
        const keyPath = this.getStoreKeyPath(storeName);
        if (!keyPath || typeof keyPath !== 'string') {
            return new Observable((observer) => {
                observer.error(new Error(`upsertRecords requires store "${storeName}" to have a single-field keyPath.`));
            });
        }
        const keyValuePairs = {};
        for (let i = 0; i < data.length; i++) {
            const value = data[i];
            const key = value[keyPath];
            if (typeof key === 'undefined' || key === null) {
                return new Observable((observer) => {
                    observer.error(new Error(`Record missing keyPath "${keyPath}" for store "${storeName}" at index ${i}.`));
                });
            }
            keyValuePairs[String(key)] = value;
        }
        // Delegate to shared batch logic
        return this.setCacheBatch(storeName, keyValuePairs);
    }
}
